import { insertElements } from '@udecode/plate';
import { removeNodes } from '@udecode/plate-common';
import { ELEMENT_TD, ELEMENT_TR } from '@udecode/plate-table';

export const withNormalizeUnbalancedTables = (editor) => {
  const { normalizeNode } = editor;

  // Cells need a block-element child (paragraph), not a raw text node.
  // Using editor.blockFactory() matches whatever the editor's default block type is.
  const makeEmptyCell = () => ({
    type: ELEMENT_TD,
    children: [editor.blockFactory()],
  });

  editor.normalizeNode = ([node, path]) => {
    // We don't want to rebalance if there are other operations running.
    if (editor.operations.length === 0 && editor.isBlock(node) && node.type === 'table') {
      const rows = node.children;

      // Create a map to track spanned cells
      const spanMap = new Map();

      // Need to iterate once through the table to get the max number of columns
      // and build the span map
      const maxColLength = Math.max(...rows.map((row, rowIndex) => {
        const { children: cols } = row;
        let colCount = 0;

        cols.forEach((col) => {
          const colSpan = parseInt(col.colSpan, 10) || 1;
          const rowSpan = parseInt(col.rowSpan, 10) || 1;

          // Track cells that span multiple rows
          if (rowSpan > 1) {
            for (let i = 1; i < rowSpan; i += 1) {
              const targetRow = rowIndex + i;
              if (!spanMap.has(targetRow)) {
                spanMap.set(targetRow, []);
              }
              // Store the column spans that affect this row
              spanMap.get(targetRow).push(colSpan);
            }
          }
          colCount += colSpan;
        });
        return colCount;
      }));

      let shouldReturn = false;
      rows.forEach((row, rowIdx) => {
        const { children: cols, type } = row;
        if (type !== ELEMENT_TR) {
          return;
        }

        // Calculate effective column length including spanned cells
        const spannedCells = spanMap.get(rowIdx) || [];
        const spannedColCount = spannedCells.reduce((sum, span) => sum + span, 0);

        const colLength = cols.reduce((acc, col) => {
          const colSpan = parseInt(col.colSpan, 10) || 1;
          return acc + colSpan;
        }, 0);

        // Account for spanned cells when calculating needed cells
        const effectiveColLength = colLength + spannedColCount;

        // When cols need to be added to balance table.
        if (effectiveColLength < maxColLength) {
          const difference = maxColLength - effectiveColLength;
          for (let i = 0; i < difference; i += 1) {
            insertElements(editor, makeEmptyCell(), {
              at: [path[0], rowIdx, cols.length + i],
            });
          }
          // Ensure we return out of the normalization so that the whole routine can
          // run again so these operations don't cascade from the recursive normalizeNode call.
          shouldReturn = true;
        }
        // When cols need to be deleted to balance table.
        else if (effectiveColLength > maxColLength) {
          const difference = effectiveColLength - maxColLength;
          for (let i = 0; i < difference; i += 1) {
            removeNodes(editor, { at: [path[0], rowIdx, cols.length - 1] });
            shouldReturn = true;
          }
        }
      });
      if (shouldReturn) return;
    }

    return normalizeNode([node, path]);
  };

  return editor;

};
